<template lang="html">
  <div class="full-height-page">
    <!-- Loading -->
    <TaxReturnLoading v-if="!taxReturn" />

    <div v-else-if="hasMultipleIdentities && !currentIdentity">
      <SelectIdentity
        :selectable-identities="selectableIdentities"
        :identity-verifications-enabled="
          taxReturn.identity_verifications_enabled
        "
        @select-identity="onSelectIdentity"
      />
    </div>

    <div v-else-if="isIdentityVerificationRequired">
      <!-- In some cases, current identity is not loaded yet, so we need to wait -->
      <VerifyIdentity
        v-if="currentIdentity"
        :tax-return-id="taxReturn.id"
        :company-id="taxReturn.company_id"
        :current-identity="currentIdentity"
        @identity-verified="onIdentityVerified"
        @back="onBack"
      />

      <TaxReturnLoading v-else />
    </div>

    <div v-else-if="isStripeIDVRequired">
      <StripeIDV
        :tax-return-id="taxReturn.id"
        :current-identity="currentUser"
        @identity-verified="onIdentityVerified"
        @back="onBack"
      />
    </div>

    <div v-else class="content-wrapper">
      <!-- Partial: Stepper Navigation -->
      <v-stepper v-model="currentStepIndex" class="stepper">
        <!-- Partial: Stepper header -->
        <nav class="stepper__header">
          <sc-button-group>
            <sc-button
              type="secondary"
              @click="backToDashboard"
              size="small"
              icon-left="arrow-left"
              :is-round="true"
              :is-icon-only="true"
            >
            </sc-button>
          </sc-button-group>

          <v-stepper-header>
            <v-layout class="header-container">
              <template v-for="(step, index) in steps">
                <v-stepper-step
                  :complete="currentStepIndex > index + 1"
                  :editable="
                    currentStepIndex > index + 1 || taxReturn.all_signed
                  "
                  :step="index + 1"
                  edit-icon="done"
                  @click="goToStep(index + 1)"
                >
                  {{ step.name }}
                </v-stepper-step>
                <v-divider v-if="index !== steps.length - 1"></v-divider>
              </template>
            </v-layout>
          </v-stepper-header>
        </nav>
      </v-stepper>

      <!-- Partial: Stepper content -->
      <div v-if="currentStep">
        <ReviewStep
          v-if="currentStep.key === 'review'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :has-next-step="hasNextStep"
          @finished-reviewing="onFinishedReviewing"
        />
        <PayInvoiceStep
          v-else-if="currentStep.key === 'pay-invoice'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :has-next-step="hasNextStep"
          @tax-return-updated="onTaxReturnUpdated"
          @next="goToNextStep"
        />
        <ESignStep
          v-else-if="currentStep.key === 'e-sign'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :current-company="currentCompany"
          :has-next-step="hasNextStep"
          :current-identity="currentIdentity"
          :session-id="sessionId"
          @tax-return-updated="onTaxReturnUpdated"
          @finished-signing="onFinishedSigning"
        />
        <DownloadAndSignStep
          v-else-if="currentStep.key === 'wet-sign'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :current-company="currentCompany"
          :current-identity="currentIdentity"
          :has-next-step="hasNextStep"
          @tax-return-updated="onTaxReturnUpdated"
          @finished-signing="onFinishedSigning"
        />
        <SignAndMailStep
          v-else-if="currentStep.key === 'paper-filed-returns'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :has-next-step="hasNextStep"
          @tax-return-updated="onTaxReturnUpdated"
          @finished-paper-filing="onFinishedPaperFiling"
        />
        <PayStep
          v-else-if="currentStep.key === 'pay'"
          :tax-return="taxReturn"
          :company-id="companyId"
          :exclude-invoice-payment="taxReturnPaymentBeforeSigningEnabled"
          @tax-return-updated="onTaxReturnUpdated"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ReviewStep from './ReviewStep';
import ESignStep from './ESignStep';
import DownloadAndSignStep from './DownloadAndSignStep';
import SignAndMailStep from './SignAndMailStep';
import PayStep from './PayStep';
import PayInvoiceStep from './PayInvoiceStep';
import axios from 'axios';
import API from '@/api';
import SelectIdentity from '@/pages/tax-return-delivery/components/SelectIdentity';
import VerifyIdentity from '@/pages/tax-return-delivery/components/VerifyIdentity';
import StripeIDV from '@/pages/tax-return-delivery/components/StripeIDV';
import { mapGetters } from 'vuex';
import TaxReturnLoading from '@/pages/tax-return-delivery/components/TaxReturnLoading';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  components: {
    ReviewStep,
    ESignStep,
    DownloadAndSignStep,
    SignAndMailStep,
    PayStep,
    PayInvoiceStep,
    SelectIdentity,
    VerifyIdentity,
    StripeIDV,
    TaxReturnLoading,
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      currentCompany: null,
      currentStepIndex: 0,
      taxReturn: null,
      steps: [],

      currentIdentity: null,
      identityVerified: false,
      sessionId: null,
    };
  },

  methods: {
    goToStep(step) {
      if (this.currentStepIndex < step) {
        return;
      }

      this.currentStepIndex = step;
    },

    onFinishedReviewing() {
      this.goToNextStep();
    },

    onFinishedSigning() {
      this.goToNextStep();
    },

    onFinishedPaperFiling() {
      this.goToNextStep();
    },

    onTaxReturnUpdated(taxReturn) {
      this.taxReturn = taxReturn;
    },

    goToNextStep() {
      if (this.currentStepIndex < this.steps.length) {
        this.currentStepIndex += 1;
      }
    },

    goToStepByKey(key) {
      const stepIndex = this.steps.findIndex(step => step.key === key);

      if (stepIndex !== -1) {
        this.goToStep(stepIndex + 1);
      }
    },

    async getTaxReturn() {
      const response = await axios.get(`/tax_returns/${this.taxReturnId}`, {
        params: {
          company_id: this.companyId,
        },
      });

      this.taxReturn = response.data;
    },

    async getCurrentCompany() {
      const response = await API.company.getCompany(this.companyId);
      this.currentCompany = response.data;
    },

    onSelectIdentity(identity) {
      this.currentIdentity = identity;
    },

    onBack() {
      this.currentIdentity = null;

      // If there are no multiple identities to select, go back to the previous page
      if (!this.hasMultipleIdentities) {
        this.$router.back();
      }
    },

    onIdentityVerified(sessionId) {
      this.identityVerified = true;
      this.sessionId = sessionId;
    },

    backToDashboard() {
      if (this.isEmbeddedInNext) {
        window.location.href = '/';
      } else {
        this.$router.push({ name: 'dashboard' });
      }
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    taxReturnId() {
      return this.$route.params.id;
    },

    companyId() {
      return this.$route.query.companyId;
    },

    currentStep() {
      return this.steps[this.currentStepIndex - 1];
    },

    hasNextStep() {
      return this.currentStepIndex < this.steps.length;
    },

    isIdentityVerificationRequired() {
      return (
        !this.currentUser.is_advisor &&
        !this.identityVerified &&
        this.taxReturn.identity_verification_enabled
      );
    },

    isStripeIDVRequired() {
      return (
        !this.currentUser.is_advisor &&
        !this.currentUser.active_identity_verification &&
        !this.identityVerified &&
        this.taxReturn.stripe_idv_enabled
      );
    },

    hasMultipleIdentities() {
      return this.selectableIdentities.length > 1;
    },

    selectableIdentities() {
      // Selectable identities are the ones that belong to the current user
      return this.taxReturn.tax_return_identity_verifications.filter(
        identity => {
          return this.currentUser.id === identity.user_id;
        },
      );
    },
  },

  watch: {
    taxReturn() {
      if (!this.taxReturn) {
        this.steps = [];
      } else {
        this.steps = [];

        const hasTaxReturnPages = this.taxReturn.tax_return_pages.length > 0;

        if (hasTaxReturnPages) {
          this.steps.push({
            key: 'review',
            name: 'Review tax return',
          });
        }

        const hasInvoicePayment = this.taxReturn.payment;

        if (hasInvoicePayment && this.taxReturnPaymentBeforeSigningEnabled) {
          this.steps.push({
            key: 'pay-invoice',
            name: 'Pay invoice',
          });
        }

        const hasEsignForms =
          this.taxReturn.e_sign_forms.filter(
            form => !form.wetSignaturesRequired,
          ).length > 0;

        if (hasEsignForms) {
          this.steps.push({
            key: 'e-sign',
            name: 'Sign electronically',
          });
        }

        const hasWetSignForms =
          this.taxReturn.e_sign_forms
            .filter(form => {
              return form.signatures.some(
                signature => signature.userId === this.currentUser.id,
              );
            })
            .filter(form => form.wetSignaturesRequired).length > 0;

        if (hasWetSignForms && this.taxReturn.status !== 'finished') {
          this.steps.push({
            key: 'wet-sign',
            name: 'Sign on papers',
          });
        }

        const hasPaperFiledReturn =
          this.taxReturn.paper_filed_returns &&
          this.taxReturn.paper_filed_returns.length > 0;

        if (hasPaperFiledReturn) {
          this.steps.push({
            key: 'paper-filed-returns',
            name: 'Sign & mail',
          });
        }

        const hasPaymentVouchers = this.taxReturn.payment_vouchers.length > 0;

        if (
          hasPaymentVouchers ||
          (hasInvoicePayment && !this.taxReturnPaymentBeforeSigningEnabled)
        ) {
          this.steps.push({
            key: 'pay',
            name: 'Make payments',
          });
        }

        const step = this.steps.findIndex(
          step => step.key === this.$route.query.step,
        );

        if (step) {
          this.currentStepIndex = step + 1;
        } else if (this.currentStepIndex === 0) {
          this.currentStepIndex = 1;
        }
      }
    },

    currentStepIndex() {
      // Update the URL with the current step so that the user can refresh the page
      // without losing the current step
      this.$router.replace({
        query: {
          ...this.$route.query,
          step: this.currentStep.key,
        },
      });
    },
  },

  async created() {
    await this.getCurrentCompany();
    await this.getTaxReturn();

    if (this.taxReturn) {
      // If there's only one selectable identity, select it by default
      if (this.selectableIdentities.length === 1) {
        this.currentIdentity = this.selectableIdentities[0];
      }
    }
  },
};
</script>

<style lang="css" scoped>
.full-height-page {
  min-height: 100dvh;

  background-color: var(--gray-0);
}

.content-wrapper {
  width: 100%;
}

.stepper {
  position: sticky;
  top: 0;
  z-index: 1;
}

.stepper__header {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 16px !important;
}

.v-stepper__header {
  box-shadow: 0 0 0 transparent;
}
</style>
